'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { reportError } from '@/lib/error-reporting';

/**
 * Route-level error boundary.
 *
 * Catches render/runtime failures inside the route segment, reports them to
 * /api/errors, and gives the user a recoverable path (retry) instead of the
 * blank white screen Next.js would otherwise render. Never shows the raw error
 * message to the user — the reference id below is what support can trace.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const reference = error.digest ?? 'unavailable';

  useEffect(() => {
    void reportError({
      message: error.message,
      source: 'boundary',
      stack: error.stack,
    });
  }, [error]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="w-14 h-14 mx-auto mb-6 rounded-2xl bg-destructive/15 border border-destructive/30 flex items-center justify-center">
          <svg
            className="w-7 h-7 text-destructive"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
        </div>

        <h1 className="text-2xl font-semibold text-foreground mb-2">Something went wrong</h1>
        <p className="text-sm text-muted-foreground mb-6">
          This section failed to load. Your data is safe — try again, and if it keeps happening, let
          us know with the reference below.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            Try again
          </button>
          <Link
            href="/dashboard"
            className="inline-flex items-center justify-center gap-2 border border-border px-5 py-2.5 rounded-lg font-medium text-foreground hover:bg-accent transition-colors"
          >
            Back to dashboard
          </Link>
        </div>

        <p className="text-xs text-muted-foreground">
          Reference: <code className="font-mono">{reference}</code>
        </p>
      </div>
    </div>
  );
}
